<template>
    <div v-if="threads">
        <div class="d-flex flex-column flex-lg-row justify-content-lg-between">
            <div>
                <h1 class="mb-3">
                    Messages
                </h1>
                <p class="mb-5">
                    View all of your message conversations in the below inbox
                </p>
            </div>

            <div>
                <div class="message-key">
                    <div><span class="key-item key-item--read" />Message Read</div>
                    <div><span class="key-item key-item--unread" />Message Unread</div>
                    <div><span class="key-item key-item--admin" />Admin Chain</div>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-lg-end mt-5 mt-lg-0 mb-5">
            <router-link
                :to="{
                    name: 'new-message-thread',
                    params: {
                        toType: 'support'
                    }
                }"
                class="btn btn--primary"
            >
                Contact Support
            </router-link>
        </div>

        <div
            v-if="threads"
            class="card"
        >
            <!-- Table heading -->
            <div class="d-flex align-items-center justify-content-between px-4 py-5">
                <h3>Inbox</h3>
                <div
                    v-if="threads.total"
                    class="d-flex align-items-center"
                >
                    <span class="mr-4">{{ threads.from }}-{{ threads.to }} of {{ threads.total }}</span>
                    <span class="d-flex align-items-center">
                        <router-link
                            :to="{
                                name: 'messages',
                                params: { page: (threads.current_page - 1) > 1 ? threads.current_page - 1 : null }
                            }"
                            :disabled="!prevButtonActive"
                            tag="button"
                            class="btn--inherit"
                        >
                            <SVGSprite
                                :class="{ 'arrow-icon-disabled': !prevButtonActive }"
                                name="arrow-square-left"
                                class="arrow-icon"
                            />
                        </router-link>
                        <router-link
                            :to="{ name: 'messages', params: { page: threads.current_page + 1 } }"
                            :disabled="!nextButtonActive"
                            tag="button"
                            class="btn--inherit"
                        >
                            <SVGSprite
                                :class="{ 'arrow-icon-disabled': !nextButtonActive }"
                                name="arrow-square-right"
                                class="arrow-icon"
                            />
                        </router-link>
                    </span>
                </div>
            </div>
            <!-- END Table heading -->

            <!-- Table listing -->
            <div
                v-if="threads.total >= 1"
                id="table-messages"
                class="table-flex table-flex--plain table-flex--cell-1-xs table-flex--cell-2-lg table-flex--cell-5-md table-flex--cell-6-lg table-flex--cell-7-md"
            >
                <div class="table-flex--header">
                    <div />
                    <div>Name</div>
                    <div>Subject</div>
                    <div>Property</div>
                    <div>Messages</div>
                    <div>Last Message</div>
                    <div />
                </div>
                <div
                    v-for="thread in threads.data"
                    :key="thread.id"
                    :class="{ 'message-unread': thread.unread_messages, 'message-admin': thread.is_admin_thread }"
                    class="message-item"
                >
                    <div />
                    <div class="text-bold color-dark-grey">
                        {{ thread.creator.last_name }}, {{ thread.creator.first_name }}
                    </div>
                    <div>{{ thread.subject }}</div>
                    <div>
                        <router-link
                            v-if="thread.property"
                            :to="{
                                name: 'view-property',
                                params: {
                                    id: thread.property.id
                                }
                            }"
                        >
                            {{ thread.property.full_address }}
                        </router-link>
                        <span v-else>
                            No Property
                        </span>
                    </div>
                    <div class="d-none d-lg-flex">
                        {{ thread.messages_count }}
                    </div>
                    <div>{{ thread.updated_at | moment('DD/MM/YY, hh:mm') }}</div>
                    <div class="d-flex justify-content-end">
                        <router-link
                            :to="{ name: 'message', params: { threadId: thread.id } }"
                            class="btn btn--secondary btn--nomin btn--sm"
                        >
                            View
                        </router-link>
                    </div>
                </div>
            </div>
            <div
                v-else
                class="px-4 pb-4"
            >
                <p>Your inbox is currently empty</p>
            </div>
            <!-- END Table listing -->

            <!-- Table Footer -->
            <div class="d-flex align-items-center justify-content-between px-4 py-5">
                <div />
                <div
                    v-if="threads.total"
                    class="d-flex align-items-center"
                >
                    <span class="mr-4">{{ threads.from }}-{{ threads.to }} of {{ threads.total }}</span>
                    <span class="d-flex align-items-center">
                        <router-link
                            :to="{
                                name: 'messages',
                                params: { page: (threads.current_page - 1) > 1 ? threads.current_page - 1 : null }
                            }"
                            :disabled="!prevButtonActive"
                            tag="button"
                            class="btn--inherit"
                        >
                            <SVGSprite
                                :class="{ 'arrow-icon-disabled': !prevButtonActive }"
                                name="arrow-square-left"
                                class="arrow-icon"
                            />
                        </router-link>
                        <router-link
                            :to="{ name: 'messages', params: { page: threads.current_page + 1 } }"
                            :disabled="!nextButtonActive"
                            tag="button"
                            class="btn--inherit"
                        >
                            <SVGSprite
                                :class="{ 'arrow-icon-disabled': !nextButtonActive }"
                                name="arrow-square-right"
                                class="arrow-icon"
                            />
                        </router-link>
                    </span>
                </div>
            </div>
            <!-- END Table Footer -->
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
    name: 'Messages',
    computed: {
        ...mapState('messages', ['threads']),
        prevButtonActive() {
            return this.threads.current_page > 1;
        },
        nextButtonActive() {
            return this.threads.current_page > 0 && this.threads.current_page < this.threads.last_page;
        }
    },
    mounted() {
        this.getThreads(this.$route.params.page || 1);
    },
    methods: {
        ...mapActions('messages', ['getThreads'])
    },
    beforeRouteUpdate(to, from, next) {
        this.getThreads(to.params.page || 1)
            .then(() => {
                next();
            });
    }
};
</script>

<style lang="scss">
.message-item {
    position: relative;

    @include media-breakpoint-down(md) {
        padding-left: 30px;
        padding-bottom: 20px;

        > div {
            &:first-of-type {
                display: none;
            }
        }
    }

    &::before {
        $size: 16px;
        content: "";
        width: $size;
        height: $size;
        border-radius: 50%;
        position: absolute;
        top: 15px;
        left: 15px;
        background: #dbdbdb;

        @include media-breakpoint-up(lg) {
            transform: translateY(-50%);
            top: 50%;
            left: 20px;
        }
    }

    &.message-admin {
        &::after {
            $size: 8px;
            content: "";
            width: $size;
            height: $size * 2;
            border-bottom-right-radius: $size * 2;
            border-top-right-radius: $size * 2;
            position: absolute;
            top: 15px;
            left: 23px;
            background: theme-color('primary');

            @include media-breakpoint-up(lg) {
                transform: translateY(-50%);
                top: 50%;
                left: 28px;
            }
        }
    }

    &.message-unread {
        &::before {
            background: theme-color('secondary');
        }
    }
}

.message-key {
    display: flex;
    align-items: flex-start;
    flex-flow: column;

    @include media-breakpoint-up(lg) {
        align-items: flex-end;
    }

    > div {
        min-width: 15rem;

        .key-item {
            margin-right: 1rem;
        }
    }
}

.key-item {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #dbdbdb;

    &--unread {
        background: theme-color('secondary');
    }

    &--admin {
        background: theme-color('primary');
    }
}
</style>
